import Blockquote from '@tiptap/extension-blockquote'

/**
 * CustomBlockquote extension inheriting from Tiptap's standard Blockquote extension.
 * Supports multiple blockquote types (chorus, verse, intro, bridge, prelude, solo, note, finishing)
 * with specific CSS color styles and a vertical text type label.
 */
export const CustomBlockquote = Blockquote.extend({
  name: 'customBlockquote',

  parseHTML() {
    return [
      {
        tag: 'blockquote[data-type]',
      },
      {
        tag: 'blockquote.custom-blockquote',
      },
      {
        tag: 'blockquote',
      },
    ]
  },

  addAttributes() {
    return {
      type: {
        default: 'chorus',
        parseHTML: element => {
          // Studio emits numbered sections as data-type="verse 1": keep the category only (CSS keys on q-verse).
          if (element.getAttribute('data-type')) return element.getAttribute('data-type').replace(/[\s_-]+\d+$/, '');
          const classList = Array.from(element.classList || []);
          const qClass = classList.find(c => c.startsWith('q-'));
          if (qClass) return qClass.replace('q-', '');
          return 'chorus';
        },
        renderHTML: attributes => ({
          'data-type': attributes.type,
          class: `custom-blockquote q-${attributes.type}`,
        }),
      },
    }
  },

  addCommands() {
    return {
      toggleCustomBlockquote: (type) => ({ commands, state, dispatch }) => {
        const { selection, tr } = state
        const { $from } = selection
        
        let depth = $from.depth
        let insideBlockquote = false
        let existingType = null
        
        while (depth > 0) {
          const node = $from.node(depth)
          if (node.type.name === this.name) {
            insideBlockquote = true
            existingType = node.attrs.type
            break
          }
          depth--
        }
        
        if (insideBlockquote) {
          if (existingType === type) {
            // Remove blockquote (unwrap)
            return commands.lift(this.name)
          } else {
            // Update type attribute of the existing blockquote node
            return commands.updateAttributes(this.name, { type })
          }
        }
        
        if (dispatch) {
          let newTr = tr
          const { from, to } = selection
          
          // 1. Check if there is a hardBreak immediately before selection start
          const nodeBefore = newTr.doc.nodeAt($from.pos - 1)
          if (nodeBefore && nodeBefore.type.name === 'hardBreak') {
            try {
              newTr = newTr.delete($from.pos - 1, $from.pos)
              newTr = newTr.split($from.pos - 1)
            } catch (e) {
              // Ignore splitting error if not allowed by schema
            }
          }
          
          // Map selection end position because of potential modification
          let mappedTo = newTr.mapping.map(to)
          
          // 2. Check if there is a hardBreak immediately at selection end
          const nodeAtTo = newTr.doc.nodeAt(mappedTo)
          if (nodeAtTo && nodeAtTo.type.name === 'hardBreak') {
            try {
              newTr = newTr.delete(mappedTo, mappedTo + 1)
              newTr = newTr.split(mappedTo)
            } catch (e) {
              // Ignore splitting error if not allowed by schema
            }
          }
          
          // Map selection range after boundary splits
          const startPos = newTr.mapping.map(from)
          const endPos = newTr.mapping.map(to)
          
          // 3. Find all hardBreak nodes inside the selection range
          const internalBreaks = []
          newTr.doc.nodesBetween(startPos, endPos, (node, pos) => {
            if (node.type.name === 'hardBreak') {
              internalBreaks.push(pos)
            }
          })
          
          // Split internal breaks (backwards to preserve positions)
          for (let i = internalBreaks.length - 1; i >= 0; i--) {
            const pos = internalBreaks[i]
            try {
              newTr = newTr.delete(pos, pos + 1)
              newTr = newTr.split(pos)
            } catch (e) {
              // Ignore splitting error if not allowed by schema
            }
          }
          
          // 4. Fallback: split at block boundaries if we didn't split them using hardBreak
          const newFrom = newTr.selection.$from
          const newTo = newTr.selection.$to
          
          if (newTo.parentOffset < newTo.parent.content.size && newTo.parentOffset > 0) {
            try {
              newTr = newTr.split(newTo.pos)
            } catch (e) {
              // Ignore splitting error if not allowed by schema
            }
          }
          
          if (newFrom.parentOffset > 0 && newFrom.parentOffset < newFrom.parent.content.size) {
            try {
              const mappedFrom = newTr.mapping.map(newFrom.pos)
              newTr = newTr.split(mappedFrom)
            } catch (e) {
              // Ignore splitting error if not allowed by schema
            }
          }
          
          // Wrap the isolated block range in customBlockquote node.
          // Map original selection positions to find the exact range.
          const finalFrom = newTr.mapping.map(from)
          const finalTo = newTr.mapping.map(to)
          const $finalFrom = newTr.doc.resolve(finalFrom)
          const $finalTo = newTr.doc.resolve(finalTo)
          
          // Adjust selection end position to remain inside the target block 
          // and prevent wrapping the subsequent block if mapped directly to it.
          let adjustedTo = finalTo
          if ($finalTo.parentOffset === 0 && adjustedTo > 0) {
            adjustedTo = adjustedTo - 1
          }
          const $adjustedTo = newTr.doc.resolve(adjustedTo)
          
          const range = $finalFrom.blockRange($adjustedTo)
          if (range) {
            const wrapping = [{ type: state.schema.nodes[this.name], attrs: { type } }]
            newTr = newTr.wrap(range, wrapping)
          }
          
          dispatch(newTr)
          return true
        }
        
        // Wrap selection in customBlockquote (fallback)
        return commands.wrapIn(this.name, { type })
      },
      unsetCustomBlockquote: () => ({ commands }) => {
        return commands.lift(this.name)
      }
    }
  }
})
